'use client';

import { clsx } from 'clsx';
import RcPagination from 'rc-pagination';

import {
  clientSideMessages,
  getLocalePrefix,
  useTranslations,
} from '@monoimin/intl';
import { useMediaQuery } from '@monoimin/hooks';
import { Locales } from '@monoimin/sb-types';
import { Icons } from '@monoimin/atoms';

import PaginationButton from './PaginationButton';

export function Pagination({
  totalItemsCount,
  locale,
  currentPageSlug,
  pagePrefix,
  perPage,
  buttonVariant = 'black',
}: {
  totalItemsCount: number;
  locale: Locales;
  currentPageSlug: string[];
  pagePrefix: string;
  perPage: number;

  buttonVariant?: 'black' | 'blue';
}) {
  const getMessage = useTranslations(clientSideMessages[locale]);

  const localePrefix = getLocalePrefix(locale);
  const pageNumber = Number.isInteger(
    +currentPageSlug[currentPageSlug.length - 1]
  )
    ? +currentPageSlug[currentPageSlug.length - 1]
    : 1;
  const isFirstPage = pageNumber == 1;
  const totalPages = Math.ceil(totalItemsCount / perPage);
  const isLast = totalPages <= pageNumber;
  const isMobile = useMediaQuery(`(max-width: 767px)`);

  const getFullQuery = (pageNumber: number) => {
    const slug = [...currentPageSlug];
    if (Number.isInteger(+currentPageSlug[currentPageSlug.length - 1])) {
      slug.pop();
    }
    const pageNumberPostfix = `${pageNumber > 1 ? pageNumber : ''}`;

    return `${localePrefix}/${pagePrefix ? `${pagePrefix}/` : ''}${
      slug.length ? slug.join('/') + '/' : ''
    }${pageNumberPostfix}`;
  };

  return (
    <nav className='mt-12'>
      <RcPagination
        hideOnSinglePage
        total={totalItemsCount}
        pageSize={perPage}
        current={+pageNumber}
        showLessItems
        defaultCurrent={1}
        onChange={() => null}
        className='flex items-center gap-2.5'
        itemRender={(page, type) => {
          const isActivePage = +pageNumber === +page;

          if (type === 'page') {
            if (isMobile) {
              if (
                +pageNumber > 2 &&
                +pageNumber < totalPages - 1 &&
                page !== totalPages &&
                page !== 1 &&
                !isActivePage
              ) {
                return (
                  <button disabled onClick={(event) => event.preventDefault()}>
                    ...
                  </button>
                );
              } else {
                return (
                  <PaginationButton
                    aria-selected={isActivePage}
                    href={getFullQuery(page)}
                    className={clsx({
                      'text-white': isActivePage,
                      'aria-selected:bg-black-500': buttonVariant === 'black',
                      'aria-selected:bg-blue-500': buttonVariant === 'blue',
                    })}
                  >
                    {page}
                  </PaginationButton>
                );
              }
            } else {
              return (
                <div className='flex  gap-2.5'>
                  {totalPages > 4 && totalPages === page && !isActivePage && (
                    <button disabled>...</button>
                  )}
                  <PaginationButton
                    aria-selected={isActivePage}
                    href={getFullQuery(page)}
                    className={clsx({
                      'text-white': isActivePage,
                      'aria-selected:bg-black-500': buttonVariant === 'black',
                      'aria-selected:bg-blue-500': buttonVariant === 'blue',
                    })}
                  >
                    {page}
                  </PaginationButton>
                  {totalPages > 4 && 1 === page && !isActivePage && (
                    <button disabled>...</button>
                  )}
                </div>
              );
            }
          }

          if (type === 'next' && !isLast) {
            return (
              <PaginationButton
                href={getFullQuery(page)}
                className='md:px-8'
                rel='next'
              >
                <span className='hidden md:inline'>
                  {getMessage('nextPage')}
                </span>
                <Icons icon='dropdown' className='-rotate-90 md:hidden' />
              </PaginationButton>
            );
          }
          if (type === 'prev' && !isFirstPage) {
            return (
              <PaginationButton
                href={getFullQuery(page)}
                className='md:px-8'
                rel='prev'
              >
                <span className='hidden md:inline'>
                  {getMessage('prevPage')}
                </span>
                <Icons icon='dropdown' className='rotate-90 md:hidden' />
              </PaginationButton>
            );
          }
          return null;
        }}
      />
    </nav>
  );
}

export default Pagination;
